import classNames from "classnames";
import React from "react";
import { CSS_CLASSES } from "./progress-indicator-css-classes";
import { ProgressIndicatorSize, ProgressIndicatorVariantProps } from "./progress-indicator.types";

const indicatorSizeOptions: { [sizeKey in ProgressIndicatorSize]: number } = {
  large: 64,
  medium: 40,
  small: 24,
};

const strokeWidthOptions: { [sizeKey in ProgressIndicatorSize]: number } = {
  large: 4,
  medium: 4,
  small: 2,
};

export const ProgressIndicatorCircular: React.FC<ProgressIndicatorVariantProps> = ({
  value,
  size = "medium",
  color = "primary",
  className,
}) => {
  const circleParameters = React.useMemo(() => {
    const r = (indicatorSizeOptions[size] - strokeWidthOptions[size]) / 2;
    const circumference = Math.round(Math.PI * (r * 2) * 100) / 100;
    return {
      dashoffset: value === undefined ? undefined : Math.round(((100 - value) / 100) * circumference * 100) / 100,
      strokeWidth: strokeWidthOptions[size],
      perimeter: circumference,
      r,
      c: indicatorSizeOptions[size] / 2,
      size: indicatorSizeOptions[size],
    };
  }, [size, value]);

  const trackClassName = classNames(CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR_TRACK, {
    [CSS_CLASSES.PROGRESS_INDICATOR_INTERACTIVE_TRACK]: color === "interactive",
    [CSS_CLASSES.PROGRESS_INDICATOR_PRIMARY_TRACK]: color === "primary",
    [CSS_CLASSES.PROGRESS_INDICATOR_SECONDARY_TRACK]: color === "secondary",
    [CSS_CLASSES.PROGRESS_INDICATOR_ACCENT_TRACK]: color === "accent",
    [CSS_CLASSES.PROGRESS_INDICATOR_GRAY_TRACK]: color === "gray",
    [CSS_CLASSES.PROGRESS_INDICATOR_ERROR_TRACK]: color === "error",
    [CSS_CLASSES.PROGRESS_INDICATOR_WARNING_TRACK]: color === "warning",
    [CSS_CLASSES.PROGRESS_INDICATOR_SUCCESS_TRACK]: color === "success",
    [CSS_CLASSES.PROGRESS_INDICATOR_WHITE_TRACK]: color === "white",
  });

  const barClassName = classNames(CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR_BAR, {
    [CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR_INDETERMINATE_SMALL_BAR]:
      circleParameters.dashoffset === undefined && size === "small",
    [CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR_INDETERMINATE_MEDIUM_BAR]:
      circleParameters.dashoffset === undefined && size === "medium",
    [CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR_INDETERMINATE_LARGE_BAR]:
      circleParameters.dashoffset === undefined && size === "large",
    [CSS_CLASSES.PROGRESS_INDICATOR_INTERACTIVE_BAR]: color === "interactive",
    [CSS_CLASSES.PROGRESS_INDICATOR_PRIMARY_BAR]: color === "primary",
    [CSS_CLASSES.PROGRESS_INDICATOR_SECONDARY_BAR]: color === "secondary",
    [CSS_CLASSES.PROGRESS_INDICATOR_ACCENT_BAR]: color === "accent",
    [CSS_CLASSES.PROGRESS_INDICATOR_GRAY_BAR]: color === "gray",
    [CSS_CLASSES.PROGRESS_INDICATOR_ERROR_BAR]: color === "error",
    [CSS_CLASSES.PROGRESS_INDICATOR_WARNING_BAR]: color === "warning",
    [CSS_CLASSES.PROGRESS_INDICATOR_SUCCESS_BAR]: color === "success",
    [CSS_CLASSES.PROGRESS_INDICATOR_WHITE_BAR]: color === "white",
  });

  return (
    <svg
      id="svg" // @todo - check and remove
      width={circleParameters.size}
      height={circleParameters.size}
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      className={classNames(
        {
          [CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR_INDETERMINATE]: value === undefined,
        },
        className
      )}
    >
      <circle
        className={trackClassName}
        r={circleParameters.r}
        strokeWidth={circleParameters.strokeWidth}
        cx={circleParameters.c}
        cy={circleParameters.c}
        fill="transparent"
        strokeDasharray={circleParameters.perimeter}
        strokeDashoffset="0"
      ></circle>
      <circle
        className={barClassName}
        r={circleParameters.r}
        strokeWidth={circleParameters.strokeWidth}
        cx={-circleParameters.c}
        cy={circleParameters.c}
        fill="transparent"
        strokeDasharray={circleParameters.perimeter}
        strokeDashoffset="0"
        style={{ strokeDashoffset: circleParameters.dashoffset }}
        transform="rotate(-90)"
      ></circle>
    </svg>
  );
};
